import React, { useState } from 'react';
import {
  Users,
  UserPlus,
  Mail,
  Search,
  MoreVertical,
  Shield,
  Check,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Trash2,
  Edit2,
  RefreshCw,
  User,
  Crown,
  Eye,
} from 'lucide-react';
import { useAuth, ROLES } from '../context/AuthContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';
import { useTranslation } from '../context/LanguageContext';
import { useProfiles, useProfileMutations, useInvitations, useInvitationMutations } from '../hooks';

const Team = () => {
  const { user, hasRole, isLoading: authLoading } = useAuth();
  const { addLog } = useActivityLog();
  const { t } = useTranslation();


  const { profiles, loading: profilesLoading, refetch: refetchProfiles } = useProfiles();
  const { updateProfile, deleteProfile } = useProfileMutations();
  const { invitations: invitationsData, loading: invitationsLoading, refetch: refetchInvitations } = useInvitations();
  const { createInvitation, cancelInvitation, resendInvitation } = useInvitationMutations();

  const [activeTab, setActiveTab] = useState('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropdownId, setShowDropdownId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);


  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: ROLES.LENDER,
    message: '',
  });

  const canManageTeam = hasRole(ROLES.AGENT);
  const canManageRoles = hasRole(ROLES.AGENT);


  const teamMembers = profiles.map(p => ({
    id: p.id,
    email: p.email,
    firstName: p.first_name,
    lastName: p.last_name,
    role: p.role,
    status: 'active',
    joinedAt: p.created_at,
    lastActive: p.last_login || p.created_at,
    avatar: null,
  }));


  const invitations = (invitationsData || []).map(inv => ({
    id: inv.id,
    email: inv.email,
    role: inv.role,
    invitedBy: 'System',
    invitedAt: inv.created_at,
    expiresAt: inv.expires_at,
    status: inv.accepted_at ? 'accepted' : new Date(inv.expires_at) < new Date() ? 'expired' : 'pending',
  }));

  const dataLoading = authLoading || profilesLoading || invitationsLoading;


  const filteredMembers = teamMembers.filter((member) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      member.firstName?.toLowerCase().includes(search) ||
      member.lastName?.toLowerCase().includes(search) ||
      member.email?.toLowerCase().includes(search)
    );
  });


  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const filteredInvitations = pendingInvitations.filter((inv) => {
    if (!searchQuery) return true;
    return inv.email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getRoleInfo = (role) => {
    const roles = {
      [ROLES.AGENT]: {
        label: t('roles.agent') || 'Facility Agent',
        color: 'bg-purple-100 text-purple-700',
        icon: Crown,
        description: t('roles.agentDesc') || 'Full platform administration and covenant management',
      },
      [ROLES.LENDER]: {
        label: t('roles.lender') || 'Lender',
        color: 'bg-blue-100 text-blue-700',
        icon: Shield,
        description: t('roles.lenderDesc') || 'Monitor loans and covenants on your exposures',
      },
      [ROLES.BORROWER]: {
        label: t('roles.borrower') || 'Borrower',
        color: 'bg-green-100 text-green-700',
        icon: User,
        description: t('roles.borrowerDesc') || 'Manage your loans and update financial data',
      },
    };
    return roles[role] || { label: role, color: 'bg-slate-100 text-slate-700', icon: Eye, description: '' };
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getLastActiveText = (lastActive) => {
    if (!lastActive) return t('team.neverActive') || 'Never';
    const diff = Date.now() - new Date(lastActive).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 5) return t('team.online');
    if (minutes < 60) return t('team.minutesAgo').replace('{minutes}', minutes).replace('{count}', minutes);
    if (hours < 24) return t('team.hoursAgo').replace('{hours}', hours).replace('{count}', hours);
    return t('team.daysAgo').replace('{days}', days).replace('{count}', days);
  };


  const handleInvite = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!inviteForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) {
      setMessage({ type: 'error', text: t('team.emailInvalid') });
      setIsLoading(false);
      return;
    }


    const result = await createInvitation(inviteForm.email, inviteForm.role, user.id, {
      inviterName: `${user.firstName} ${user.lastName}`,
      inviterEmail: user.email,
      companyName: user.company || 'Covenant Tracker',
      message: inviteForm.message || undefined,
      language: document.documentElement.lang || 'fr',
    });

    if (result.success) {
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.TEAM,
        action: 'member_invited',
        message: `Invitation envoyée à ${inviteForm.email}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        details: { invitedEmail: inviteForm.email, role: inviteForm.role },
      });

      setMessage({ type: 'success', text: `${t('team.invitationSentTo')} ${inviteForm.email}` });
      setInviteForm({ email: '', role: ROLES.LENDER, message: '' });
      setShowInviteModal(false);
      refetchInvitations();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setIsLoading(false);
  };


  const handleUpdateRole = async (memberId, newRole) => {
    setIsLoading(true);

    const member = teamMembers.find((m) => m.id === memberId);
    const oldRole = member?.role;

    const result = await updateProfile(memberId, { role: newRole });

    if (result.success) {
      addLog({
        type: LOG_TYPES.CONFIG,
        category: LOG_CATEGORIES.TEAM,
        action: 'role_changed',
        message: `Rôle modifié: ${member?.firstName} ${member?.lastName} → ${getRoleInfo(newRole).label}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        details: { targetUserId: memberId, oldRole, newRole },
      });

      setMessage({ type: 'success', text: t('team.roleUpdatedSuccess') });
      setShowEditModal(false);
      setSelectedMember(null);
      refetchProfiles();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setIsLoading(false);
  };


  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
    setShowDropdownId(null);
  };


  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    setIsLoading(true);

    const result = await deleteProfile(memberToDelete.id);

    if (result.success) {
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.TEAM,
        action: 'member_removed',
        message: `Membre retiré: ${memberToDelete.firstName} ${memberToDelete.lastName}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        details: { removedUserId: memberToDelete.id, removedEmail: memberToDelete.email },
      });

      setMessage({ type: 'success', text: t('team.memberRemovedSuccess') });
      setShowDeleteModal(false);
      setMemberToDelete(null);
      refetchProfiles();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setIsLoading(false);
  };


  const handleCancelInvitation = async (invitationId) => {
    setIsLoading(true);

    const invitation = invitations.find((i) => i.id === invitationId);
    const result = await cancelInvitation(invitationId);

    if (result.success) {
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.TEAM,
        action: 'invitation_cancelled',
        message: `Invitation annulée: ${invitation?.email}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        details: { cancelledEmail: invitation?.email },
      });

      setMessage({ type: 'success', text: t('team.invitationCancelledSuccess') });
      refetchInvitations();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setIsLoading(false);
  };


  const handleResendInvitation = async (invitationId) => {
    setIsLoading(true);

    const invitation = invitations.find((i) => i.id === invitationId);


    const result = await resendInvitation(invitationId, {
      inviterName: `${user.firstName} ${user.lastName}`,
      inviterEmail: user.email,
      companyName: user.company || 'Covenant Tracker',
      language: document.documentElement.lang || 'fr',
    });

    if (result.success) {
      addLog({
        type: LOG_TYPES.ACTION,
        category: LOG_CATEGORIES.TEAM,
        action: 'invitation_resent',
        message: `Invitation renvoyée à ${invitation?.email}`,
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userEmail: user.email,
        details: { resentEmail: invitation?.email, role: invitation?.role },
      });

      setMessage({ type: 'success', text: t('team.invitationResentSuccess') });
      refetchInvitations();
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setIsLoading(false);
  };

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <Users className="w-7 h-7 text-blue-600" />
              {t('team.title')}
            </h1>
            <p className="text-slate-500 mt-1">
              {teamMembers.length} {teamMembers.length > 1 ? t('team.membersCountPlural') : t('team.membersCount')} · {pendingInvitations.length} {t('team.pendingInvitations')}
            </p>
          </div>

          {canManageTeam && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              {t('team.inviteMember')}
            </button>
          )}
        </div>

        {}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{message.text}</p>
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('members')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'members'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t('team.members')} ({teamMembers.length})
                </button>
                <button
                  onClick={() => setActiveTab('invitations')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'invitations'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t('team.invitations')} ({pendingInvitations.length})
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('team.searchPlaceholder')}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
                />
              </div>
            </div>
          </div>

          {}
          {activeTab === 'members' && (
            <div className="divide-y divide-slate-200">
              {filteredMembers.map((member) => {
                const roleInfo = getRoleInfo(member.role);
                const isCurrentUser = member.id === user?.id?.toString();

                return (
                  <div
                    key={member.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                          {getInitials(member.firstName, member.lastName)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">
                              {member.firstName} {member.lastName}
                            </p>
                            {isCurrentUser && (
                              <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                                {t('team.you')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{member.email}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded ${roleInfo.color}`}
                            >
                              {roleInfo.label}
                            </span>
                            <span className="text-xs text-slate-400">
                              {t('team.memberSince')} {formatDate(member.joinedAt)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                          <p
                            className={`text-sm font-medium ${
                              getLastActiveText(member.lastActive) === t('team.online')
                                ? 'text-green-600'
                                : 'text-slate-500'
                            }`}
                          >
                            {getLastActiveText(member.lastActive)}
                          </p>
                        </div>

                        {canManageTeam && !isCurrentUser && (
                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowDropdownId(showDropdownId === member.id ? null : member.id)
                              }
                              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-slate-500" />
                            </button>

                            {showDropdownId === member.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                                {canManageRoles && (
                                  <button
                                    onClick={() => {
                                      setSelectedMember(member);
                                      setShowEditModal(true);
                                      setShowDropdownId(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    {t('team.editRole')}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteClick(member)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t('team.removeFromTeam')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredMembers.length === 0 && (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t('team.noMembersFound')}</p>
                </div>
              )}
            </div>
          )}

          {}
          {activeTab === 'invitations' && (
            <div className="divide-y divide-slate-200">
              {filteredInvitations.map((invitation) => {
                const roleInfo = getRoleInfo(invitation.role);
                const isExpired = new Date(invitation.expiresAt) < new Date();

                return (
                  <div
                    key={invitation.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{invitation.email}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded ${roleInfo.color}`}
                            >
                              {roleInfo.label}
                            </span>
                            <span className="text-xs text-slate-400">
                              {t('team.invitedBy')} {invitation.invitedBy}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {isExpired ? (
                              <span className="text-red-500">{t('team.invitationExpired')}</span>
                            ) : (
                              <>{t('team.expiresOn')} {formatDate(invitation.expiresAt)}</>
                            )}
                          </p>
                        </div>
                      </div>

                      {canManageTeam && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleResendInvitation(invitation.id)}
                            disabled={isLoading}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            {t('team.resend')}
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id)}
                            disabled={isLoading}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                            {t('team.cancel')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {filteredInvitations.length === 0 && (
                <div className="p-8 text-center">
                  <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t('team.noInvitations')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">{t('team.availableRoles')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.values(ROLES).map((role) => {
              const info = getRoleInfo(role);
              const RoleIcon = info.icon;
              const count = teamMembers.filter((m) => m.role === role).length;

              return (
                <div
                  key={role}
                  className="p-4 border border-slate-200 rounded-lg hover:border-blue-200 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info.color}`}>
                      <RoleIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{info.label}</h3>
                      <p className="text-xs text-slate-500">{count} {count > 1 ? t('team.membersCountPlural') : t('team.membersCount')}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">{info.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">{t('team.inviteModal.title')}</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <form onSubmit={handleInvite} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('team.inviteModal.emailLabel')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('team.inviteModal.emailPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('team.inviteModal.roleLabel')}
                </label>
                <div className="space-y-2">
                  {Object.values(ROLES)
                    .map((role) => {
                      const info = getRoleInfo(role);
                      const RoleIcon = info.icon;

                      return (
                        <label
                          key={role}
                          className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            inviteForm.role === role
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role}
                            checked={inviteForm.role === role}
                            onChange={(e) =>
                              setInviteForm((prev) => ({ ...prev, role: e.target.value }))
                            }
                            className="sr-only"
                          />
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${info.color}`}>
                            <RoleIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{info.label}</p>
                            <p className="text-xs text-slate-500">{info.description}</p>
                          </div>
                          {inviteForm.role === role && (
                            <Check className="w-5 h-5 text-blue-600" />
                          )}
                        </label>
                      );
                    })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('team.inviteModal.messageLabel')}
                </label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  placeholder={t('team.inviteModal.messagePlaceholder')}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 py-3 px-4 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {t('team.inviteModal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('team.inviteModal.sending')}
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      {t('team.inviteModal.send')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">{t('team.editRoleModal.title')}</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMember(null);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                  {getInitials(selectedMember.firstName, selectedMember.lastName)}
                </div>
                <div>
                  <p className="font-medium text-slate-800">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </p>
                  <p className="text-sm text-slate-500">{selectedMember.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                {Object.values(ROLES).map((role) => {
                  const info = getRoleInfo(role);
                  const RoleIcon = info.icon;
                  const isCurrentRole = selectedMember.role === role;

                  return (
                    <button
                      key={role}
                      onClick={() => handleUpdateRole(selectedMember.id, role)}
                      disabled={isLoading || isCurrentRole}
                      className={`w-full flex items-center p-3 border-2 rounded-lg transition-all ${
                        isCurrentRole
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${info.color}`}>
                        <RoleIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-slate-800">{info.label}</p>
                        <p className="text-xs text-slate-500">{info.description}</p>
                      </div>
                      {isCurrentRole && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                          {t('team.editRoleModal.current')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">{t('team.deleteModal.title')}</h2>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {}
              <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                  {getInitials(memberToDelete.firstName, memberToDelete.lastName)}
                </div>
                <div>
                  <p className="font-medium text-slate-800">
                    {memberToDelete.firstName} {memberToDelete.lastName}
                  </p>
                  <p className="text-sm text-slate-500">{memberToDelete.email}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${getRoleInfo(memberToDelete.role).color}`}>
                    {getRoleInfo(memberToDelete.role).label}
                  </span>
                </div>
              </div>

              {}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-700 font-medium">{t('team.deleteModal.message')}</p>
                    <p className="text-sm text-red-600 mt-1">{t('team.deleteModal.warning')}</p>
                  </div>
                </div>
              </div>

              {}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="flex-1 py-3 px-4 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      {t('team.deleteModal.confirm')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;